import type { Writable } from "svelte/store";
import type { UloztoConfig } from "./fs";
import { FS } from "./fs";
import { FsDir } from "./fsentry";
import type { HttpProgress } from "./http";
import { mkURL } from "./http";
import { throwIf } from "./util";

// Uloz.to does not answer other origins. Calls go through /api/ulozto. The
// app token, the user name and the password are request headers. What is
// deleted goes to the bin of Uloz.to.

class FileSystemUlozto extends FS {
  supportsDirRename = true;

  constructor(config: UloztoConfig) {
    super(config);
  }

  get uloztoConfig(): UloztoConfig {
    return this.config as UloztoConfig;
  }

  async readDir(dirPath: string, force: boolean, progress: Writable<any>): Promise<FsDir> {
    throwIf(dirPath == "", "empty dir");
    return this.loadDir(dirPath, force, progress, async () => {
      let js = await uloztoCall(this.uloztoConfig, mkURL("/api/ulozto/list", "dir", dirPath));
      if (!Array.isArray(js)) {
        throw new Error("ulozto list failed");
      }
      return FsDir.fromWire(dirPath, js);
    });
  }

  async getPublicURL(path: string): Promise<string> {
    // Preview and the download link cannot send the API key. Fetch the file
    // and hand the bytes back as a blob URL.
    let blob = await this.downloadFile(path, () => {});
    return URL.createObjectURL(blob);
  }

  async downloadFile(path: string, httpProgress: HttpProgress): Promise<Blob> {
    let url = mkURL("/api/ulozto/download", "path", path);
    let xhr = await uloztoSend("GET", url, this.uloztoConfig, undefined, httpProgress, "blob");
    return xhr.response as Blob;
  }

  async uploadFile(path: string, file: Blob, httpProgress: HttpProgress): Promise<void> {
    let url = mkURL("/api/ulozto/upload", "path", path);
    await uloztoSend("PUT", url, this.uloztoConfig, file, httpProgress);
  }

  async rename(path: string, newPath: string): Promise<void> {
    throwIf(path == newPath, "path must be != newPath");
    let url = mkURL("/api/ulozto/rename", "path", path, "newPath", newPath);
    await uloztoCall(this.uloztoConfig, url, "POST");
  }

  async createDirectory(dirPath: string): Promise<void> {
    await uloztoCall(this.uloztoConfig, mkURL("/api/ulozto/mkdir", "path", dirPath), "POST");
  }

  // A folder goes with everything in it.
  async deleteFile(path: string): Promise<void> {
    await uloztoCall(this.uloztoConfig, mkURL("/api/ulozto/delete", "path", path), "POST");
  }
}

// The user name and the password are percent-encoded: a header cannot carry
// every character.
export function uloztoHeaders(config: UloztoConfig): Record<string, string> {
  return {
    "X-Ulozto-App-Token": config.appToken,
    "X-Ulozto-Username": encodeURIComponent(config.username),
    "X-Ulozto-Password": encodeURIComponent(config.password),
  };
}

export async function testUloztoConnection(config: UloztoConfig): Promise<void> {
  await uloztoCall(config, "/api/ulozto/test");
}

async function uloztoCall(config: UloztoConfig, url: string, method = "GET"): Promise<any> {
  let rsp = await fetch(url, {
    method,
    headers: uloztoHeaders(config),
  });
  let text = await rsp.text();
  let js: any = {};
  if (text) {
    try {
      js = JSON.parse(text);
    } catch {
      js = {};
    }
  }
  if (!rsp.ok) {
    throw new Error(js.error || `ulozto ${rsp.status}`);
  }
  return js;
}

function uloztoSend(
  method: string,
  url: string,
  config: UloztoConfig,
  data: Blob | undefined,
  onProgress: HttpProgress | undefined,
  responseType?: XMLHttpRequestResponseType,
): Promise<XMLHttpRequest> {
  return new Promise((resolve, reject) => {
    let xhr = new XMLHttpRequest();
    xhr.open(method, url);
    if (responseType) {
      xhr.responseType = responseType;
    }
    for (let [k, v] of Object.entries(uloztoHeaders(config))) {
      xhr.setRequestHeader(k, v);
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr);
        return;
      }
      uloztoErrText(xhr).then(
        (msg) => reject(new Error(msg)),
        () => reject(new Error(`ulozto ${xhr.status}`)),
      );
    };
    xhr.onerror = () => {
      reject(new Error("ulozto request failed"));
    };
    if (onProgress) {
      let started = Date.now();
      let report = (ev: ProgressEvent) => {
        let total = ev.lengthComputable ? ev.total : 0;
        onProgress(ev.loaded, total, Date.now() - started);
      };
      if (method == "PUT") {
        xhr.upload.onprogress = report;
      } else {
        xhr.onprogress = report;
      }
    }
    if (data) {
      xhr.send(data);
    } else {
      xhr.send();
    }
  });
}

async function uloztoErrText(xhr: XMLHttpRequest): Promise<string> {
  let text = "";
  if (xhr.responseType == "blob" && xhr.response instanceof Blob) {
    text = await xhr.response.text();
  } else if (xhr.responseType == "" || xhr.responseType == "text") {
    text = xhr.responseText || "";
  }
  if (text) {
    try {
      let js = JSON.parse(text);
      if (js.error) {
        return String(js.error);
      }
    } catch {
      // keep the status
    }
  }
  return `ulozto ${xhr.status}`;
}

export function makeFileSystemUlozto(config: UloztoConfig): FileSystemUlozto {
  return new FileSystemUlozto(config);
}
